import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'
import {
  MoreHorizontal,
  Pause,
  Pencil,
  Play,
  Trash2,
  Workflow,
} from 'lucide-react'
import { useGraphsClient } from '@/features/node-agents/use-graphs-client'
import { pathFor } from '@/lib/routes'
import { EmptyState } from '@/components/ui/empty-state'
import type { Graph, GraphSummary } from '@super/shared-types'
import {
  DELINQUENCY_GRAPH_TEMPLATE,
  LEASE_RENEWAL_GRAPH_TEMPLATE,
  LEASING_FOLLOW_UP_GRAPH_TEMPLATE,
  NEW_OWNER_LEAD_GRAPH_TEMPLATE,
  OWNER_INTAKE_GRAPH_TEMPLATE,
  PEST_CONTROL_GRAPH_TEMPLATE,
  SHOWING_SCHEDULER_GRAPH_TEMPLATE,
} from '@super/shared-types'
import { cn } from '@/lib/utils'
import { withFreshIds } from '@super/node-graph-ui'

const TEMPLATE_OPTIONS: {
  id: string
  label: string
  description: string
  graph: Graph
}[] = [
  {
    id: 'leasing-inquiry',
    label: 'Leasing inquiry',
    description:
      'Helps a prospect who has no home in mind yet: asks for bedrooms and budget, shares the homes that match, and answers their questions.',
    graph: LEASING_FOLLOW_UP_GRAPH_TEMPLATE,
  },
  {
    id: 'showing-scheduler',
    label: 'Showing scheduler',
    description:
      'Calls a prospect about one home and books a showing at a time that works for them.',
    graph: SHOWING_SCHEDULER_GRAPH_TEMPLATE,
  },
  {
    id: 'delinquency',
    label: 'Delinquency follow-up',
    description:
      'Checks in with a resident about a past-due balance, asks when they expect to pay, and answers their questions. A dispute is noted for the team.',
    graph: DELINQUENCY_GRAPH_TEMPLATE,
  },
  {
    id: 'pest-control',
    label: 'Pest control follow-up',
    description:
      'Reminds the resident of the upcoming service, asks about pets and notes for the technician, then answers their questions from the knowledge base.',
    graph: PEST_CONTROL_GRAPH_TEMPLATE,
  },
  {
    id: 'lease-renewal',
    label: 'Lease renewal',
    description:
      "Checks in on a resident's renewal plans with two quick questions, then answers theirs from the knowledge base.",
    graph: LEASE_RENEWAL_GRAPH_TEMPLATE,
  },
  {
    id: 'new-owner-lead',
    label: 'New owner lead',
    description:
      'Asks an owner lead for zip, unit count, current management and timeline, and the property situation, then books a sales call and collects last details for the team. A request for a person or a lead who is not interested is noted for the team.',
    graph: NEW_OWNER_LEAD_GRAPH_TEMPLATE,
  },
  {
    id: 'owner-intake',
    label: 'Owner intake',
    description:
      'Takes eight intake questions from an owner lead, one of each answer type, and answers questions between them from the knowledge you attach before going back to the form. A primary home gets its own short talk.',
    graph: OWNER_INTAKE_GRAPH_TEMPLATE,
  },
]

function CreateNodeAgentDialog({
  open,
  onOpenChange,
  onCreate,
  creating,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (name: string, graph: Graph) => void
  creating: boolean
}) {
  const [name, setName] = useState('')
  const [templateId, setTemplateId] = useState(TEMPLATE_OPTIONS[0].id)

  const submit = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    const template = TEMPLATE_OPTIONS.find(t => t.id === templateId)
    onCreate(trimmed, template?.graph ?? TEMPLATE_OPTIONS[0].graph)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New node agent</DialogTitle>
          <DialogDescription>
            A node agent is a calling agent whose conversation runs as a
            sequence of steps. Name it and pick a starting template — you can
            edit everything after.
          </DialogDescription>
        </DialogHeader>
        <Input
          placeholder="Agent name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          autoFocus
        />
        <div className="space-y-2">
          {TEMPLATE_OPTIONS.map(template => {
            const selected = templateId === template.id
            return (
              <button
                key={template.id}
                type="button"
                onClick={() => setTemplateId(template.id)}
                className={cn(
                  'w-full flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                  selected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted/50',
                )}
              >
                <span
                  className={cn(
                    'mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 flex-shrink-0',
                    selected ? 'border-primary' : 'border-muted-foreground/30',
                  )}
                >
                  {selected && (
                    <span className="h-2 w-2 rounded-full bg-primary" />
                  )}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">
                    {template.label}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {template.description}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="form"
            size="sm"
            onClick={submit}
            disabled={creating || !name.trim()}
          >
            {creating ? 'Creating…' : 'Create agent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function NodeAgentsListPage() {
  useDocumentHead({
    title: 'Node Agents',
    description: 'Agents driven by a node graph of phase-scoped prompts',
  })

  const navigate = useNavigate()
  const client = useGraphsClient()
  const [records, setRecords] = useState<GraphSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const result = await client.list()
        // Defensive: never let an unexpected payload shape crash the route.
        if (!Array.isArray(result)) {
          console.error(
            'graphs list returned a non-array payload:',
            JSON.stringify(result).slice(0, 500),
          )
        }
        setRecords(Array.isArray(result) ? result : [])
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [client])

  const handleToggleActive = async (record: GraphSummary) => {
    try {
      const full = await client.get(record.id)
      if (!full) {
        toast.error('Could not load node agent')
        return
      }
      const nextActive = !record.active
      const saved = await client.save(record.id, {
        ...full,
        active: nextActive,
      })
      if (!saved) {
        toast.error('Failed to update node agent')
        return
      }
      toast.success(
        nextActive ? 'Node agent activated' : 'Node agent deactivated',
      )
      setRecords(prev =>
        prev.map(r => (r.id === record.id ? { ...r, active: nextActive } : r)),
      )
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to update node agent',
      )
    }
  }

  const handleDelete = async (record: GraphSummary) => {
    if (!window.confirm(`Delete "${record.name}"?`)) return
    try {
      const deleted = await client.remove(record.id)
      if (deleted) {
        toast.success('Node agent deleted')
        setRecords(prev => prev.filter(r => r.id !== record.id))
      } else {
        toast.error('Failed to delete node agent')
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to delete node agent',
      )
    }
  }

  const handleCreate = async (name: string, graph: Graph) => {
    setCreating(true)
    try {
      // The ids in a template are placeholders, and the API stores the ones it
      // is sent. Without fresh ones the second agent made from a template would
      // ask for rows the first already holds.
      const created = await client.create({ ...withFreshIds(graph), name })
      if (created) {
        toast.success('Node agent created')
        navigate(pathFor('NODE_AGENTS_DETAIL', { id: created.id }))
      } else {
        toast.error('Failed to create node agent')
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to create node agent'
      toast.error(message)
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 pb-24">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-6">
          <PageHeader title="Node Agents" />
          <Button
            onClick={() => setCreateOpen(true)}
            variant="form"
            size="sm"
            className="h-9 w-fit shrink-0"
          >
            New node agent
          </Button>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : records.length === 0 ? (
          <EmptyState
            icon={<Workflow className="h-8 w-8 text-muted-foreground" />}
            title="No node agents yet"
            description="A node agent is a calling agent whose conversation runs as a sequence of steps. Create one — it starts with an example two-step flow (Qualify → Schedule) you can edit."
            actionLabel="Create your first node agent"
            onAction={() => setCreateOpen(true)}
            actionButtonVariant="default"
          />
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden lg:table-cell">Status</TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Created
                  </TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Updated at
                  </TableHead>
                  <TableHead className="w-[70px]" align="center">
                    Options
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map(record => {
                  const openDetail = () =>
                    navigate(pathFor('NODE_AGENTS_DETAIL', { id: record.id }))
                  return (
                    <TableRow
                      key={record.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={openDetail}
                    >
                      <TableCell>
                        <div className="font-medium">{record.name}</div>
                        <div className="lg:hidden text-sm text-muted-foreground">
                          {record.active ? 'Active' : 'Inactive'}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <Badge
                          variant={record.active ? 'default' : 'secondary'}
                          className={cn(
                            'text-xs',
                            record.active
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                              : 'bg-muted text-muted-foreground',
                          )}
                        >
                          {record.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-muted-foreground">
                        {new Date(record.createdAt).toLocaleDateString(
                          'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          },
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-muted-foreground">
                        {new Date(record.updatedAt).toLocaleDateString(
                          'en-US',
                          {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          },
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={e => e.stopPropagation()}
                            >
                              <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={e => {
                                e.stopPropagation()
                                openDetail()
                              }}
                            >
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit agent
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={e => {
                                e.stopPropagation()
                                handleToggleActive(record)
                              }}
                            >
                              {record.active ? (
                                <>
                                  <Pause className="w-4 h-4 mr-2" />
                                  Deactivate agent
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 mr-2" />
                                  Activate agent
                                </>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={e => {
                                e.stopPropagation()
                                handleDelete(record)
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete agent
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <CreateNodeAgentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
        creating={creating}
      />
    </div>
  )
}
